import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { describeMeasurementAutoDetect } from "../../../utils/measurementQuestionDetect";

const TOOL_LABEL = { angle: "Protractor", length: "Ruler", construct: "Full construction set" };

/** geometry_config -> the fields this picker edits. */
export const boardStateFromConfig = (cfg) => ({
  boardEnabled: cfg?.kind === "construct_measure",
  boardExtent: Number(cfg?.board_extent_cm) || 12,
  unknowns: Array.isArray(cfg?.unknowns) ? cfg.unknowns.join(", ") : "",
  givens: Array.isArray(cfg?.given)
    ? cfg.given
        .map((g) =>
          g?.type === "angle" ? `${g.label || "angle"} ${g.deg}°`
            : g?.type === "side" ? `${g.label || "side"} ${g.cm} cm`
              : String(g?.label || "")
        )
        .filter(Boolean)
        .join("; ")
    : "",
});

/**
 * The fields back into a geometry_config, preserving any key this picker does not own —
 * `pixels_per_cm` in particular calibrates the on-screen ruler and belongs to questions that have no
 * construction board at all, so turning the board off must not take it with it.
 */
export const configFromBoardState = ({ boardEnabled, boardExtent, unknowns, givens }, existing) => {
  const { kind: _k, board_extent_cm: _b, unknowns: _u, given: _g, ...rest } = existing || {};
  if (!boardEnabled) {
    return Object.keys(rest).length ? rest : null;
  }
  return {
    ...rest,
    kind: "construct_measure",
    board_extent_cm: Number(boardExtent) || 12,
    unknowns: String(unknowns || "").split(",").map((s) => s.trim()).filter(Boolean),
    given: String(givens || "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((part) => {
        const ang = part.match(/^(.*?)\s+(\d+(?:\.\d+)?)\s*°$/);
        if (ang) return { type: "angle", label: ang[1].trim() || "angle", deg: parseFloat(ang[2]) };
        const side = part.match(/^(.*?)\s+(\d+(?:\.\d+)?)\s*cm$/i);
        if (side) return { type: "side", label: side[1].trim() || "side", cm: parseFloat(side[2]) };
        return { type: "note", label: part };
      }),
  };
};

/**
 * Which instrument a question — or ONE part of a grouped question — puts in the student's hand, and
 * whether it also gets the calibrated Construction Board.
 *
 * Used at the top level and per part, because the two need the same choices: ISEB 2009-10 Q8 is one
 * question whose (i) constructs, (ii) wants a protractor, (iii) wants a ruler and (iv) wants neither.
 * The part's own value wins over the parent's at read time (measurementModeForSub /
 * shouldShowConstructBoard).
 */
export default function MeasurementToolPicker({
  tool,
  onToolChange,
  boardEnabled,
  boardExtent,
  unknowns,
  givens,
  onBoardChange,
  autoDetectFor = null,
  label = "Measurement tool for student",
  inheritLabel = "Auto (detect from question text)",
}) {
  const auto = tool === "auto" && autoDetectFor ? describeMeasurementAutoDetect(autoDetectFor) : null;
  const set = (patch) => onBoardChange({ boardEnabled, boardExtent, unknowns, givens, ...patch });

  return (
    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5, maxWidth: 460 }}>
      <FormControl size="small" sx={{ maxWidth: 360 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={tool}
          onChange={(e) => {
            const v = e.target.value;
            onToolChange(v);
            if (v === "construct") set({ boardEnabled: true });
          }}
        >
          <MenuItem value="auto">{inheritLabel}</MenuItem>
          <MenuItem value="none">None (no measurement tools)</MenuItem>
          <MenuItem value="angle">Protractor only (angle)</MenuItem>
          <MenuItem value="length">Ruler only (length)</MenuItem>
          <MenuItem value="construct">Full construction set (protractor + ruler + compass + set square)</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 460 }}>
        Works for MCQ and structured items. Choose <strong>None</strong> when the diagram already
        shows a printed ruler/protractor (Reading scales). Choose Protractor/Ruler when the student
        must use an on-screen tool even if auto-detect does not fire.
      </Typography>

      {tool === "construct" ? (
        <Box sx={{ mt: 1, p: 1.5, border: "1px solid #c7d2fe", borderRadius: 1, bgcolor: "#f8fafc", display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox size="small" checked={!!boardEnabled} onChange={(e) => set({ boardEnabled: e.target.checked })} />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Also show calibrated construct board (for rough-drawing / draw-accurately items)
              </Typography>
            }
          />
          <Typography variant="caption" color="text.secondary">
            Keeps Diagram tools (protractor, ruler, compass, set square). The board is rough work — the
            marks come from the typed answer or the drawing canvas, not from what is built on it.
          </Typography>
          {boardEnabled ? (
            <>
              <TextField
                size="small"
                label="Unknowns to find (comma-separated)"
                placeholder="A_length, B_angle"
                value={unknowns}
                onChange={(e) => set({ unknowns: e.target.value })}
              />
              <TextField
                size="small"
                label="Givens (optional; separate with ; )"
                placeholder="top-left 3.5 cm; top 65°; left 110°"
                value={givens}
                onChange={(e) => set({ givens: e.target.value })}
                helperText="Format: label N cm or label N°"
              />
              <TextField
                size="small"
                type="number"
                label="Board size (cm)"
                value={boardExtent}
                onChange={(e) => set({ boardExtent: e.target.value })}
                inputProps={{ min: 8, max: 20, step: 1 }}
                sx={{ maxWidth: 160 }}
              />
            </>
          ) : null}
        </Box>
      ) : null}

      {tool === "auto" && autoDetectFor ? (
        <Typography variant="caption" color={auto ? "primary.main" : "text.secondary"}>
          {auto
            ? `Auto-detected: ${TOOL_LABEL[auto.mode] || auto.mode}${auto.phrase ? ` — matched "${auto.phrase}"` : ""}. Pick a tool above to override.`
            : "Auto: no measurement tool detected from this text. Pick a tool above to force one."}
        </Typography>
      ) : null}
    </Box>
  );
}
